import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { forkJoin, map } from 'rxjs';
import type Stripe from 'stripe';
import { freeStripeProductPrice } from '../billing-consts';
import { minTimeBetweenTrialsInDays } from '../consts';
import { GROWTHBOOK } from '../growthbook';
import { isFreeSubscription } from '../lib/subscription-helpers';
import { AppConfigService } from '../providers/config.service';
import { Product, RecurringInterval } from '../types/product.type';
import { Promotion } from '../types/promotion.type';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { StateService } from './state.service';
import { TransferStateService } from './transfer-state.service';

export type PriceDetails = {
  basePlan: number;
  storageAddonCost: number;
  subTotal: number;
  yearlyDiscount: number;
  promoDiscount: number;
  totalDiscount: number;
  balance: number;
  total: number;
};

export interface PriceDetailsCalculation {
  monthlyAmount: number;
  monthlyAmountWithoutDiscount: number;
  yearlyAmount: number;
  yearlyAmountWithoutDiscount: number;
  monthlyDiscountAmount: number;
  displayAmountWhole: number;
  displayAmountCents: number;
}

@Injectable({
  providedIn: 'root',
})
export class PricingService {
  private growthBook = inject(GROWTHBOOK);
  private config = inject(AppConfigService);
  private http = inject(HttpClient);
  private transferState = inject(TransferStateService);
  private loader = inject(ObservableDataLoaderService);
  private state = inject(StateService);

  public getPrices(product_type: 'main' | 'storage_addon' | 'seat_addon' | 'credit_addon') {
    return this.loader
      .get('prices', () =>
        forkJoin([this.getPricesInternal('year'), this.getPricesInternal('month')]).pipe(
          map(resp => resp.flat()),
          map(prices => prices.filter(price => price.recurring.interval_count === 1))
        )
      )
      .pipe(
        map(prices => prices.filter(price => (price.product as Stripe.Product).metadata.picflow_type === product_type))
      );
  }

  private getPricesInternal(recurring_interval: 'month' | 'year') {
    const url = `${this.config.apiEndpoint}/billing/prices?limit=100&recurring_interval=${recurring_interval}`;
    return this.transferState.withStateTransferObservable(`prices_${recurring_interval}`, () =>
      this.http.get<{ data: Stripe.Price[] }>(url).pipe(map(resp => resp.data))
    );
  }

  public isDowngrade(fromPrice: Stripe.Price, to: Product) {
    if (!fromPrice || !to) return false;
    const newMonthlyPrice =
      to.prices.find(p => p.recurring.interval === fromPrice.recurring.interval) || freeStripeProductPrice;
    return newMonthlyPrice.unit_amount / 100 < fromPrice.unit_amount / 100;
  }

  public isUpgrade(fromPrice: Stripe.Price, to: Product) {
    return !this.isDowngrade(fromPrice, to);
  }

  public getTrialLengthDays() {
    return this.growthBook.getFeatureValue('trial-length', 14);
  }
  public getTrialExtensionDays() {
    return this.growthBook.getFeatureValue('trial-extension-length', 7);
  }

  getPriceDetails(
    prices: Stripe.Price[],
    promo: Promotion,
    billingFrequency: RecurringInterval
  ): PriceDetailsCalculation {
    const monthlyPrice = prices.find(price => price.recurring.interval === 'month') || freeStripeProductPrice;
    const yearlyPrice = prices.find(price => price.recurring.interval === 'year') || freeStripeProductPrice;

    let monthlyDiscountAmount = 0;
    let yearlyDiscountAmount = 0;
    if (promo?.percentOff) {
      monthlyDiscountAmount = (monthlyPrice.unit_amount / 100 / 100) * promo.percentOff;
      yearlyDiscountAmount = (yearlyPrice.unit_amount / 100 / 100) * promo.percentOff;
    }
    // Not showing fixed discount amounts here on purpose.

    const monthlyAmountWithoutDiscount = monthlyPrice.unit_amount / 100;
    const yearlyAmountWithoutDiscount = yearlyPrice.unit_amount / 100;
    const monthlyAmount = monthlyPrice.unit_amount / 100 - monthlyDiscountAmount;
    const yearlyAmount = yearlyPrice.unit_amount / 100 - yearlyDiscountAmount;

    const displayAmount = billingFrequency === 'year' ? yearlyAmount / 12 : monthlyAmount;
    const displayAmountWhole = Math.floor(displayAmount);
    const displayAmountCents = Math.round((displayAmount - displayAmountWhole) * 100);

    return {
      monthlyAmount,
      monthlyAmountWithoutDiscount,
      yearlyAmount,
      yearlyAmountWithoutDiscount,
      monthlyDiscountAmount,
      displayAmountWhole,
      displayAmountCents,
    };
  }

  public getDefaultPrice(billingFrequency: Stripe.Price.Recurring.Interval, pricesForCorrectType: Stripe.Price[] = []) {
    const defaultPriceKeys: string[] = this.growthBook.getFeatureValue('default-price-keys', []);
    return pricesForCorrectType.find(
      price => price.recurring.interval === billingFrequency && defaultPriceKeys.includes(price.lookup_key)
    );
  }

  public canStartTrial(subscription: Stripe.Subscription) {
    const trialLength = this.getTrialLengthDays();
    if (!trialLength) return false;
    if (!subscription) return false;
    if (subscription.status === 'trialing') return false;
    if (!isFreeSubscription(subscription)) return false;
    return this.commonTrialCheck();
  }

  public canExtendTrial(subscription: Stripe.Subscription) {
    if (!subscription) return false;
    if (subscription.status !== 'trialing') return false;
    const trialExtensionDays = this.getTrialExtensionDays();
    if (!trialExtensionDays) return false;
    return this.commonTrialCheck();
  }

  private commonTrialCheck() {
    const tenant = this.state.activeTenant$?.value;
    if (!tenant) return false;
    if (!tenant.trial_modified_at) return true;
    const timeSinceTrialModified = new Date().getTime() - tenant.trial_modified_at * 1000;
    const daysInceTrialModified = timeSinceTrialModified / 1000 / 60 / 60 / 24;
    return daysInceTrialModified >= minTimeBetweenTrialsInDays;
  }
}
